import * as React from 'react';
import React__default, { useMemo, useCallback, useState, useEffect, Suspense } from 'react';
import { useOidc, useOidcAccessToken, OidcProvider } from '@axa-fr/react-oidc';
export { OidcSecure, withOidcSecure } from '@axa-fr/react-oidc';
import { __rest } from 'tslib';
import { Loading, Button } from '@komune-io/g2-components';
import { useTheme, makeG2STyles } from '@komune-io/g2-themes';
import { Box, Typography } from '@mui/material';
import { initReactI18next, I18nextProvider, useTranslation } from 'react-i18next';
import { BrowserRouter, Link, useNavigate, Routes, Route } from 'react-router-dom';
import i18n from 'i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { Provider } from 'react-redux';
import { combineReducers, createStore, applyMiddleware, compose } from 'redux';

const reBindPolicieValues = (policie) => {
    const policieKeys = Object.keys(Object.getPrototypeOf(policie));
    return policieKeys.reduce((object, key) => {
        //@ts-ignore
        object[key] = policie[key].bind(policie);
        return object;
        //@ts-ignore
    }, {});
};

function useAuth(roles = [], additionalServices, policies) {
    const oidc = useOidc();
    const { isAuthenticated } = oidc;
    const { accessToken, accessTokenPayload } = useOidcAccessToken();
    const tokenParsed = accessTokenPayload;
    const keycloak = useMemo(() => (Object.assign(Object.assign({}, oidc), { token: accessToken, tokenParsed: accessTokenPayload })), [oidc, accessToken, accessTokenPayload]);
    const getPrincipalRole = useCallback((givenRoles) => {
        for (const it in roles) {
            if (givenRoles.includes(roles[it])) {
                return roles[it];
            }
        }
        return;
    }, [roles]);
    const getUserPrincipalRole = useCallback(() => {
        var _a, _b;
        //@ts-ignore
        const userRoles = (_b = (_a = tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.realm_access) === null || _a === void 0 ? void 0 : _a.roles) !== null && _b !== void 0 ? _b : [];
        return getPrincipalRole(userRoles);
    }, [tokenParsed, getPrincipalRole]);
    const hasRole = useCallback((role) => {
        var _a, _b, _c;
        if (Array.isArray(role)) {
            if (role.length === 0)
                return true;
            for (const it in role) {
                if ((_a = tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.realm_access) === null || _a === void 0 ? void 0 : _a.roles.includes(role[it]))
                    return true;
            }
            return false;
        }
        else {
            return (_c = (_b = tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.realm_access) === null || _b === void 0 ? void 0 : _b.roles.includes(role)) !== null && _c !== void 0 ? _c : false;
        }
    }, [tokenParsed]);
    const hasAllRoles = useCallback((roles) => {
        let hasAll = true;
        roles.forEach((role) => {
            if (!hasRole(role)) {
                hasAll = false;
            }
        });
        return hasAll;
    }, [hasRole]);
    const getUserId = useCallback(
    // @ts-ignore
    () => tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.sub, [tokenParsed]);
    const getUserRoles = useCallback(
    // @ts-ignore
    () => { var _a; return (_a = tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.realm_access) === null || _a === void 0 ? void 0 : _a.roles; }, [tokenParsed]);
    const getUser = useCallback(
    // @ts-ignore
    () => {
        var _a;
        if (!isAuthenticated)
            return;
        return {
            id: tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.sub,
            email: tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.email,
            memberOf: tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.memberOf,
            firstName: tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.given_name,
            lastName: tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.family_name,
            roles: (_a = tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.realm_access) === null || _a === void 0 ? void 0 : _a.roles,
            fullName: tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.name
        };
    }, [isAuthenticated, tokenParsed]);
    const isMemberOf = useCallback((organizationId) => (tokenParsed === null || tokenParsed === void 0 ? void 0 : tokenParsed.memberOf) === organizationId, [tokenParsed]);
    const executeAuthFunction = useCallback(function (authFunction, ...args) {
        const user = getUser();
        if (!user)
            return false;
        return authFunction(user, ...args);
    }, [getUser]);
    const rolesServices = useMemo(() => {
        const object = {};
        for (const it in roles) {
            const fn = () => hasRole(roles[it]);
            object[`is_${roles[it]}`] = fn;
        }
        return object;
    }, [hasRole, roles]);
    const definitivePolicies = useMemo(() => {
        if (!policies)
            return {};
        //@ts-ignore
        const res = {};
        Object.keys(policies).forEach((key) => {
            const reBinded = reBindPolicieValues(policies[key]);
            Object.keys(reBinded).forEach((policyKey) => {
                //@ts-ignore
                if (!res[key])
                    res[key] = {};
                //@ts-ignore
                res[key][policyKey] = (...args) => executeAuthFunction(reBinded[policyKey], ...args);
            });
        });
        return res;
    }, [policies, executeAuthFunction]);
    const service = useMemo(() => (Object.assign({ getUserId: getUserId, hasRole: hasRole, getUser: getUser, getUserPrincipalRole: getUserPrincipalRole, getPrincipalRole: getPrincipalRole, hasAllRoles: hasAllRoles, isMemberOf: isMemberOf, getUserRoles: getUserRoles, executeAuthFunction }, rolesServices)), [
        hasRole,
        getUserId,
        getUser,
        getUserPrincipalRole,
        rolesServices,
        getPrincipalRole,
        hasAllRoles,
        isMemberOf,
        getUserRoles,
        executeAuthFunction
    ]);
    const additionals = useMemo(() => {
        const object = {};
        for (const serviceName in additionalServices) {
            const fn = (params) => additionalServices[serviceName.toString()](keycloak, service, params);
            object[serviceName.toString()] = fn;
        }
        return object;
    }, [additionalServices, keycloak, hasRole, roles, service]);
    return {
        service: Object.assign(service, additionals),
        keycloak,
        //@ts-ignore
        policies: definitivePolicies
    };
}

const R = console;
class Oe {
  constructor(n, t, s, o = 2e3, r = !0) {
    this._callback = n, this._client_id = t, this._url = s, this._interval = o || 2e3, this._stopOnError = r;
    const i = s.indexOf("/", s.indexOf("//") + 2);
    this._frame_origin = s.substr(0, i), this._frame = window.document.createElement("iframe"), this._frame.style.visibility = "hidden", this._frame.style.position = "absolute", this._frame.style.display = "none", this._frame.width = 0, this._frame.height = 0, this._frame.src = s;
  }
  load() {
    return new Promise((n) => {
      this._frame.onload = () => {
        n();
      }, window.document.body.appendChild(this._frame), this._boundMessageEvent = this._message.bind(this), window.addEventListener("message", this._boundMessageEvent, !1);
    });
  }
  _message(n) {
    n.origin === this._frame_origin && n.source === this._frame.contentWindow && (n.data === "error" ? (R.error("CheckSessionIFrame: error message from check session op iframe"), this._stopOnError && this.stop()) : n.data === "changed" ? (R.debug(n), R.debug("CheckSessionIFrame: changed message from check session op iframe"), this.stop(), this._callback()) : R.debug("CheckSessionIFrame: " + n.data + " message from check session op iframe"));
  }
  start(n) {
    R.debug("CheckSessionIFrame.start :" + n), this.stop();
    const t = () => {
      this._frame.contentWindow.postMessage(this._client_id + " " + n, this._frame_origin);
    };
    t(), this._timer = window.setInterval(t, this._interval);
  }
  stop() {
    this._timer && (R.debug("CheckSessionIFrame.stop"), window.clearInterval(this._timer), this._timer = null);
  }
}
const p = {
  service_worker_not_supported_by_browser: "service_worker_not_supported_by_browser",
  token_aquired: "token_aquired",
  logout_from_another_tab: "logout_from_another_tab",
  logout_from_same_tab: "logout_from_same_tab",
  token_renewed: "token_renewed",
  token_timer: "token_timer",
  loginAsync_begin: "loginAsync_begin",
  loginAsync_error: "loginAsync_error",
  loginCallbackAsync_begin: "loginCallbackAsync_begin",
  loginCallbackAsync_end: "loginCallbackAsync_end",
  loginCallbackAsync_error: "loginCallbackAsync_error",
  refreshTokensAsync_begin: "refreshTokensAsync_begin",
  refreshTokensAsync: "refreshTokensAsync",
  refreshTokensAsync_end: "refreshTokensAsync_end",
  refreshTokensAsync_error: "refreshTokensAsync_error",
  refreshTokensAsync_silent_error: "refreshTokensAsync_silent_error",
  tryKeepExistingSessionAsync_begin: "tryKeepExistingSessionAsync_begin",
  tryKeepExistingSessionAsync_end: "tryKeepExistingSessionAsync_end",
  tryKeepExistingSessionAsync_error: "tryKeepExistingSessionAsync_error",
  silentLoginAsync_begin: "silentLoginAsync_begin",
  silentLoginAsync: "silentLoginAsync",
  silentLoginAsync_end: "silentLoginAsync_end",
  silentLoginAsync_error: "silentLoginAsync_error",
  syncTokensAsync_begin: "syncTokensAsync_begin",
  syncTokensAsync_lock_not_available: "syncTokensAsync_lock_not_available",
  syncTokensAsync_end: "syncTokensAsync_end",
  syncTokensAsync_error: "syncTokensAsync_error",
  tokensInvalidAndWaitingActionsToRefresh: "tokensInvalidAndWaitingActionsToRefresh"
}, I = (e, n = sessionStorage) => {
  const t = (y) => (n[`oidc.${e}`] = JSON.stringify({ tokens: null, status: y }), Promise.resolve()), s = async () => {
    if (!n[`oidc.${e}`])
      return n[`oidc.${e}`] = JSON.stringify({ tokens: null, status: null }), { tokens: null, status: null };
    const y = JSON.parse(n[`oidc.${e}`]);
    return Promise.resolve({ tokens: y.tokens, status: y.status });
  }, o = (y) => {
    n[`oidc.${e}`] = JSON.stringify({ tokens: y });
  }, r = async (y) => {
    n[`oidc.session_state.${e}`] = y;
  }, i = async () => n[`oidc.session_state.${e}`], l = (y) => {
    n[`oidc.nonce.${e}`] = y.nonce;
  }, a = (y) => {
    n[`oidc.jwk.${e}`] = JSON.stringify(y);
  }, f = () => JSON.parse(n[`oidc.jwk.${e}`]), _ = async () => ({ nonce: n[`oidc.nonce.${e}`] }), c = async (y) => {
    n[`oidc.dpop_nonce.${e}`] = y;
  }, u = () => n[`oidc.dpop_nonce.${e}`], h = () => n[`oidc.${e}`] ? JSON.stringify({ tokens: JSON.parse(n[`oidc.${e}`]).tokens }) : null;
  let g = {};
  return {
    clearAsync: t,
    initAsync: s,
    setTokens: o,
    getTokens: h,
    setSessionStateAsync: r,
    getSessionStateAsync: i,
    setNonceAsync: l,
    getNonceAsync: _,
    setLoginParams: (y) => {
      g[e] = y, n[`oidc.login.${e}`] = JSON.stringify(y);
    },
    getLoginParams: () => {
      const y = n[`oidc.login.${e}`];
      return y ? (g[e] || (g[e] = JSON.parse(y)), g[e]) : (console.warn(`storage[oidc.login.${e}] is empty, you should have an bad OIDC or code configuration somewhere.`), null);
    },
    getStateAsync: async () => n[`oidc.state.${e}`],
    setStateAsync: async (y) => {
      n[`oidc.state.${e}`] = y;
    },
    getCodeVerifierAsync: async () => n[`oidc.code_verifier.${e}`],
    setCodeVerifierAsync: async (y) => {
      n[`oidc.code_verifier.${e}`] = y;
    },
    setDemonstratingProofOfPossessionNonce: c,
    getDemonstratingProofOfPossessionNonce: u,
    setDemonstratingProofOfPossessionJwkAsync: a,
    getDemonstratingProofOfPossessionJwkAsync: f
  };
};
var V = /* @__PURE__ */ ((e) => (e.AutomaticBeforeTokenExpiration = "AutomaticBeforeTokensExpiration", e.AutomaticOnlyWhenFetchExecuted = "AutomaticOnlyWhenFetchExecuted", e))(V || {});
const Pe = (e) => decodeURIComponent(Array.prototype.map.call(atob(e), (n) => "%" + ("00" + n.charCodeAt(0).toString(16)).slice(-2)).join("")), Ie = (e) => JSON.parse(Pe(e.replaceAll(/-/g, "+").replaceAll(/_/g, "/"))), ae = (e) => {
  try {
    return e && Ce(e, ".") === 2 ? Ie(e.split(".")[1]) : null;
  } catch (n) {
    console.warn(n);
  }
  return null;
}, Ce = (e, n) => e.split(n).length - 1, Z = {
  access_token_or_id_token_invalid: "access_token_or_id_token_invalid",
  access_token_invalid: "access_token_invalid",
  id_token_invalid: "id_token_invalid"
};
function Ne(e, n, t) {
  if (e.issuedAt) {
    if (typeof e.issuedAt == "string")
      return parseInt(e.issuedAt, 10);
  } else
    return n && n.iat ? n.iat : t && t.iat ? t.iat : (/* @__PURE__ */ new Date()).getTime() / 1e3;
  return e.issuedAt;
}
const z = (e, n = null, t) => {
  if (!e)
    return null;
  let s;
  const o = typeof e.expiresIn == "string" ? parseInt(e.expiresIn, 10) : e.expiresIn;
  e.accessTokenPayload !== void 0 ? s = e.accessTokenPayload : s = ae(e.accessToken);
  let r;
  n != null && "idToken" in n && !("idToken" in e) ? r = n.idToken : r = e.idToken;
  const i = e.idTokenPayload ? e.idTokenPayload : ae(r), l = i && i.exp ? i.exp : Number.MAX_VALUE, a = s && s.exp ? s.exp : e.issuedAt + o;
  e.issuedAt = Ne(e, s, i);
  let f;
  e.expiresAt ? f = e.expiresAt : t === Z.access_token_invalid ? f = a : t === Z.id_token_invalid ? f = l : f = l < a ? l : a;
  const _ = { ...e, idTokenPayload: i, accessTokenPayload: s, expiresAt: f, idToken: r };
  if (n != null && "refreshToken" in n && !("refreshToken" in e)) {
    const c = n.refreshToken;
    return { ..._, refreshToken: c };
  }
  return _;
}, oe = (e, n, t) => {
  if (!e)
    return null;
  if (!e.issued_at) {
    const o = (/* @__PURE__ */ new Date()).getTime() / 1e3;
    e.issued_at = o;
  }
  const s = {
    accessToken: e.access_token,
    expiresIn: e.expires_in,
    idToken: e.id_token,
    scope: e.scope,
    tokenType: e.token_type,
    issuedAt: e.issued_at
  };
  return "refresh_token" in e && (s.refreshToken = e.refresh_token), e.accessTokenPayload !== void 0 && (s.accessTokenPayload = e.accessTokenPayload), e.idTokenPayload !== void 0 && (s.idTokenPayload = e.idTokenPayload), z(s, n, t);
}, U = (e, n) => {
  const t = (/* @__PURE__ */ new Date()).getTime() / 1e3, s = n - t;
  return Math.round(s - e);
}, ce = (e) => e ? U(0, e.expiresAt) > 0 : !1, ge = async (e, n = 200, t = 50) => {
  let s = t;
  if (!e.tokens)
    return null;
  for (; !ce(e.tokens) && s > 0; ) {
    if (e.configuration.token_automatic_renew_mode == V.AutomaticOnlyWhenFetchExecuted) {
      await e.renewTokensAsync({});
      break;
    } else
      await M({ milliseconds: n });
    s = s - 1;
  }
  return {
    isTokensValid: ce(e.tokens),
    tokens: e.tokens,
    numberWaited: s - t
  };
}, ke = (e, n, t) => {
  if (e.idTokenPayload) {
    const s = e.idTokenPayload;
    if (t.issuer !== s.iss)
      return { isValid: !1, reason: `Issuer does not match (oidcServerConfiguration issuer) ${t.issuer} !== (idTokenPayload issuer) ${s.iss}` };
    const o = (/* @__PURE__ */ new Date()).getTime() / 1e3;
    if (s.exp && s.exp < o)
      return { isValid: !1, reason: `Token expired (idTokenPayload exp) ${s.exp} < (currentTimeUnixSecond) ${o}` };
    const r = 60 * 60 * 24 * 7;
    if (s.iat && s.iat + r < o)
      return { isValid: !1, reason: `Token is used from too long time (idTokenPayload iat + timeInSevenDays) ${s.iat + r} < (currentTimeUnixSecond) ${o}` };
    if (s.nonce && s.nonce !== n)
      return { isValid: !1, reason: `Nonce does not match (idTokenPayload nonce) ${s.nonce} !== (nonce) ${n}` };
  }
  return { isValid: !0, reason: "" };
}, B = function() {
  const e = function() {
    let a, f;
    const _ = (function() {
      const u = {}, h = {
        setTimeout: function(m, d, w) {
          u[d] = setTimeout(function() {
            m.postMessage(d), u[d] = null;
          }, w);
        },
        setInterval: function(m, d, w) {
          u[d] = setInterval(function() {
            m.postMessage(d);
          }, w);
        },
        clearTimeout: function(m, d) {
          clearTimeout(u[d]), u[d] = null;
        },
        clearInterval: function(m, d) {
          clearInterval(u[d]), u[d] = null;
        }
      };
      function g(m, d) {
        const w = d.data[0], A = d.data[1], v = d.data[2];
        h[w] && h[w](m, A, v);
      }
      this.onmessage = function(m) {
        g(self, m);
      }, this.onconnect = function(m) {
        const d = m.ports[0];
        d.onmessage = function(w) {
          g(d, w);
        };
      };
    }).toString();
    try {
      const u = new Blob(["(", _, ")()"], { type: "application/javascript" });
      f = URL.createObjectURL(u);
    } catch {
      return null;
    }
    const c = typeof process > "u";
    try {
      if (SharedWorker)
        return a = new SharedWorker(f), a.port;
    } catch {
      c && console.warn("SharedWorker not available");
    }
    try {
      if (Worker)
        return a = new Worker(f), a;
    } catch {
      c && console.warn("Worker not available");
    }
    return null;
  }();
  if (!e) {
    const a = typeof window > "u" ? global : window;
    return {
      setTimeout: setTimeout.bind(a),
      clearTimeout: clearTimeout.bind(a),
      setInterval: setInterval.bind(a),
      clearInterval: clearInterval.bind(a)
    };
  }
  const n = /* @__PURE__ */ function() {
    let a = 0;
    return function() {
      return a++, a;
    };
  }(), t = {}, s = {};
  e.onmessage = function(a) {
    const f = a.data, _ = t[f];
    if (_) {
      _(), t[f] = null;
      return;
    }
    const c = s[f];
    c && c();
  };
  function o(a, f) {
    const _ = n();
    return e.postMessage(["setTimeout", _, f]), t[_] = a, _;
  }
  function r(a) {
    e.postMessage(["clearTimeout", a]), t[a] = null;
  }
  function i(a, f) {
    const _ = n();
    return e.postMessage(["setInterval", _, f]), s[_] = a, _;
  }
  function l(a) {
    e.postMessage(["clearInterval", a]), s[a] = null;
  }
  return {
    setTimeout: o,
    clearTimeout: r,
    setInterval: i,
    clearInterval: l
  };
}(), le = "7.22.4";
let ue = null, q;
const M = ({ milliseconds: e }) => new Promise((n) => B.setTimeout(n, e)), me = (e = "/") => {
  try {
    q = new AbortController(), fetch(`${e}OidcKeepAliveServiceWorker.json?minSleepSeconds=150`, { signal: q.signal }).catch((s) => {
      console.log(s);
    }), M({ milliseconds: 150 * 1e3 }).then(me);
  } catch (n) {
    console.log(n);
  }
}, xe = () => {
  q && q.abort();
}, We = (e = "/") => fetch(`${e}OidcKeepAliveServiceWorker.json`, {
  headers: {
    "oidc-vanilla": "true"
  }
}).then((n) => n.statusText === "oidc-service-worker").catch((n) => {
  console.log(n);
}), Le = (e) => async (n, t) => {
  t(), await n.update();
  const s = await n.unregister();
  console.log(`Service worker unregistering ${s}`), await M({ milliseconds: 2e3 }), e.reload();
}, O = (e) => (n) => new Promise(function(t, s) {
  const o = new MessageChannel();
  o.port1.onmessage = function(r) {
    r.data && r.data.error ? s(r.data.error) : t(r.data);
  }, e.active.postMessage(n, [o.port2]);
}), C = async (e, n) => {
  const t = e.service_worker_relative_url;
  if (typeof window > "u" || typeof navigator > "u" || !navigator.serviceWorker || !t || e.service_worker_activate() === !1)
    return null;
  let s = null;
  e.register ? s = await e.service_worker_register(t) : s = await navigator.serviceWorker.register(t);
  try {
    await navigator.serviceWorker.ready, navigator.serviceWorker.controller || await O(s)({ type: "claim" });
  } catch {
    return null;
  }
  const o = async (k) => O(s)({ type: "clear", data: { status: k }, configurationName: n }), r = async (k, S, P) => {
    const b = await O(s)({
      type: "init",
      data: {
        oidcServerConfiguration: k,
        where: S,
        oidcConfiguration: {
          token_renew_mode: P.token_renew_mode,
          service_worker_convert_all_requests_to_cors: P.service_worker_convert_all_requests_to_cors
        }
      },
      configurationName: n
    }), D = b.version;
    return D !== le && (console.warn(`Service worker ${D} version mismatch with js client version ${le}, unregistering and reloading`), await P.service_worker_update_require_callback(s, xe)), { tokens: oe(b.tokens, null, P.token_renew_mode), status: b.status };
  }, i = (k = "/") => {
    ue == null && (ue = "not_null", me(k));
  }, l = (k) => O(s)({ type: "setSessionState", data: { sessionState: k }, configurationName: n }), a = async () => (await O(s)({ type: "getSessionState", data: null, configurationName: n })).sessionState, f = (k) => (sessionStorage[`oidc.nonce.${n}`] = k.nonce, O(s)({ type: "setNonce", data: { nonce: k }, configurationName: n })), _ = async () => {
    let S = (await O(s)({ type: "getNonce", data: null, configurationName: n })).nonce;
    return S || (S = sessionStorage[`oidc.nonce.${n}`], console.warn("nonce not found in service worker, using sessionStorage")), { nonce: S };
  };
  let c = {};
  return {
    clearAsync: o,
    initAsync: r,
    startKeepAliveServiceWorker: () => i(e.service_worker_keep_alive_path),
    isServiceWorkerProxyActiveAsync: () => We(e.service_worker_keep_alive_path),
    setSessionStateAsync: l,
    getSessionStateAsync: a,
    setNonceAsync: f,
    getNonceAsync: _,
    setLoginParams: (k) => {
      c[n] = k, localStorage[`oidc.login.${n}`] = JSON.stringify(k);
    },
    getLoginParams: () => {
      const k = localStorage[`oidc.login.${n}`];
      return c[n] || (c[n] = JSON.parse(k)), c[n];
    },
    getStateAsync: async () => {
      let S = (await O(s)({ type: "getState", data: null, configurationName: n })).state;
      return S || (S = sessionStorage[`oidc.state.${n}`], console.warn("state not found in service worker, using sessionStorage")), S;
    },
    setStateAsync: async (k) => (sessionStorage[`oidc.state.${n}`] = k, O(s)({ type: "setState", data: { state: k }, configurationName: n })),
    getCodeVerifierAsync: async () => {
      let S = (await O(s)({ type: "getCodeVerifier", data: null, configurationName: n })).codeVerifier;
      return S || (S = sessionStorage[`oidc.code_verifier.${n}`], console.warn("codeVerifier not found in service worker, using sessionStorage")), S;
    },
    setCodeVerifierAsync: async (k) => (sessionStorage[`oidc.code_verifier.${n}`] = k, O(s)({ type: "setCodeVerifier", data: { codeVerifier: k }, configurationName: n })),
    setDemonstratingProofOfPossessionNonce: async (k) => {
      await O(s)({ type: "setDemonstratingProofOfPossessionNonce", data: { demonstratingProofOfPossessionNonce: k }, configurationName: n });
    },
    getDemonstratingProofOfPossessionNonce: async () => (await O(s)({ type: "getDemonstratingProofOfPossessionNonce", data: null, configurationName: n })).demonstratingProofOfPossessionNonce,
    setDemonstratingProofOfPossessionJwkAsync: async (k) => {
      const S = JSON.stringify(k);
      await O(s)({ type: "setDemonstratingProofOfPossessionJwk", data: { demonstratingProofOfPossessionJwkJson: S }, configurationName: n });
    },
    getDemonstratingProofOfPossessionJwkAsync: async () => {
      const k = await O(s)({ type: "getDemonstratingProofOfPossessionJwk", data: null, configurationName: n });
      return k.demonstratingProofOfPossessionJwkJson ? JSON.parse(k.demonstratingProofOfPossessionJwkJson) : null;
    }
  };
}, $ = {}, De = (e, n = window.sessionStorage, t) => {
  if (!$[e] && n) {
    const o = n.getItem(e);
    o && ($[e] = JSON.parse(o));
  }
  const s = 1e3 * t;
  return $[e] && $[e].timestamp + s > Date.now() ? $[e].result : null;
}, Re = (e, n, t = window.sessionStorage) => {
  const s = Date.now();
  $[e] = { result: n, timestamp: s }, t && t.setItem(e, JSON.stringify({ result: n, timestamp: s }));
};
function pe(e) {
  return new TextEncoder().encode(e);
}
function we(e) {
  return btoa(e).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+/g, "");
}
function $e(e) {
  return encodeURIComponent(e).replace(/%([0-9A-F]{2})/g, function(t, s) {
    return String.fromCharCode(parseInt(s, 16));
  });
}
const re = (e) => {
  let n = "";
  return e.forEach(function(t) {
    n += String.fromCharCode(t);
  }), we(n);
};
function _e(e) {
  return we($e(e));
}
const Ke = {
  importKeyAlgorithm: {
    name: "ECDSA",
    namedCurve: "P-256",
    hash: { name: "ES256" }
  },
  signAlgorithm: { name: "ECDSA", hash: { name: "SHA-256" } },
  generateKeyAlgorithm: {
    name: "ECDSA",
    namedCurve: "P-256"
  },
  digestAlgorithm: { name: "SHA-256" },
  jwtHeaderAlgorithm: "ES256"
}, Ue = (e) => async (n, t, s, o, r = "dpop+jwt") => {
  switch (n = Object.assign({}, n), t.typ = r, t.alg = o.jwtHeaderAlgorithm, t.alg) {
    case "ES256":
      t.jwk = { kty: n.kty, crv: n.crv, x: n.x, y: n.y };
      break;
    case "RS256":
      t.jwk = { kty: n.kty, n: n.n, e: n.e, kid: t.kid };
      break;
    default:
      throw new Error("Unknown or not implemented JWS algorithm");
  }
  const i = {
    // @ts-ignore
    // JWT "headers" really means JWS "protected headers"
    protected: _e(JSON.stringify(t)),
    // @ts-ignore
    // JWT "claims" are really a JSON-defined JWS "payload"
    payload: _e(JSON.stringify(s))
  }, l = o.importKeyAlgorithm, a = !0, f = ["sign"], _ = await e.crypto.subtle.importKey("jwk", n, l, a, f), c = pe(`${i.protected}.${i.payload}`), u = o.signAlgorithm, h = await e.crypto.subtle.sign(u, _, c);
  return i.signature = re(new Uint8Array(h)), `${i.protected}.${i.payload}.${i.signature}`;
};
var Fe = { sign: Ue };
const Ve = (e) => async (n) => {
  const t = n, s = !0, o = ["sign", "verify"], r = await e.crypto.subtle.generateKey(t, s, o);
  return await e.crypto.subtle.exportKey("jwk", r.privateKey);
}, Me = (e) => {
  const n = Object.assign({}, e);
  return delete n.d, n.key_ops = ["verify"], n;
}, Je = {
  generate: Ve,
  neuter: Me
}, Be = (e) => async (n, t) => {
  let s;
  switch (n.kty) {
    case "EC":
      s = '{"crv":"CRV","kty":"EC","x":"X","y":"Y"}'.replace("CRV", n.crv).replace("X", n.x).replace("Y", n.y);
      break;
    case "RSA":
      s = '{"e":"E","kty":"RSA","n":"N"}'.replace("E", n.e).replace("N", n.n);
      break;
    default:
      throw new Error("Unknown or not implemented JWK type");
  }
  const o = await e.crypto.subtle.digest(t, pe(s));
  return re(new Uint8Array(o));
};
var He = { thumbprint: Be };
const je = (e) => async (n) => await Je.generate(e)(n), Ae = (e) => (n) => async (t, s = "POST", o, r = {}) => {
  const i = {
    // https://www.rfc-editor.org/rfc/rfc9449.html#name-concept
    jti: btoa(qe()),
    htm: s,
    htu: o,
    iat: Math.round(Date.now() / 1e3),
    ...r
  }, l = await He.thumbprint(e)(t, n.digestAlgorithm);
  return await Fe.sign(e)(t, { kid: l }, i, n);
}, qe = () => {
  const e = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx", n = "0123456789abcdef";
  let t = 0, s = "";
  for (let o = 0; o < 36; o++)
    e[o] !== "-" && e[o] !== "4" && (t = Math.random() * 16 | 0), e[o] === "x" ? s += n[t] : e[o] === "y" ? (t &= 3, t |= 8, s += n[t]) : s += e[o];
  return s;
}, Se = () => {
  const e = typeof window < "u" && !!window.crypto, n = e && !!window.crypto.subtle;
  return { hasCrypto: e, hasSubtleCrypto: n };
}, ee = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789", Ge = (e) => {
  const n = [];
  for (let t = 0; t < e.byteLength; t += 1) {
    const s = e[t] % ee.length;
    n.push(ee[s]);
  }
  return n.join("");
}, ne = (e) => {
  const n = new Uint8Array(e), { hasCrypto: t } = Se();
  if (t)
    window.crypto.getRandomValues(n);
  else
    for (let s = 0; s < e; s += 1)
      n[s] = Math.random() * ee.length | 0;
  return Ge(n);
};
function Ye(e) {
  const n = new ArrayBuffer(e.length), t = new Uint8Array(n);
  for (let s = 0; s < e.length; s++)
    t[s] = e.charCodeAt(s);
  return t;
}
function ve(e) {
  return new Promise((n, t) => {
    crypto.subtle.digest("SHA-256", Ye(e)).then((s) => n(re(new Uint8Array(s))), (s) => t(s));
  });
}
const Xe = (e) => {
  if (e.length < 43 || e.length > 128)
    return Promise.reject(new Error("Invalid code length."));
  const { hasSubtleCrypto: n } = Se();
  return n ? ve(e) : Promise.reject(new Error("window.crypto.subtle is unavailable."));
}, ze = 60 * 60, Qe = (e) => async (n, t = ze, s = window.sessionStorage, o = 1e4) => {
  const r = `${n}/.well-known/openid-configuration`, i = `oidc.server:${n}`, l = De(i, s, t);
  if (l)
    return new se(l);
  const a = await H(e)(r, {}, o);
  if (a.status !== 200)
    return null;
  const f = await a.json();
  return Re(i, f, s), new se(f);
}, H = (e) => async (n, t = {}, s = 1e4, o = 0) => {
  let r;
  try {
    const i = new AbortController();
    setTimeout(() => i.abort(), s), r = await e(n, { ...t, signal: i.signal });
  } catch (i) {
    if (i.name === "AbortError" || i.message === "Network request failed") {
      if (o <= 1)
        return await H(e)(n, t, s, o + 1);
      throw i;
    } else
      throw console.error(i.message), i;
  }
  return r;
}, te = {
  refresh_token: "refresh_token",
  access_token: "access_token"
}, fe = (e) => async (n, t, s = te.refresh_token, o, r = {}, i = 1e4) => {
  const l = {
    token: t,
    token_type_hint: s,
    client_id: o
  };
  for (const [c, u] of Object.entries(r))
    l[c] === void 0 && (l[c] = u);
  const a = [];
  for (const c in l) {
    const u = encodeURIComponent(c), h = encodeURIComponent(l[c]);
    a.push(`${u}=${h}`);
  }
  const f = a.join("&");
  return (await H(e)(n, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8"
    },
    body: f
  }, i)).status !== 200 ? { success: !1 } : {
    success: !0
  };
}, Ze = (e) => async (n, t, s, o, r = {}, i, l = 1e4) => {
  for (const [h, g] of Object.entries(s))
    t[h] === void 0 && (t[h] = g);
  const a = [];
  for (const h in t) {
    const g = encodeURIComponent(h), m = encodeURIComponent(t[h]);
    a.push(`${g}=${m}`);
  }
  const f = a.join("&"), _ = await H(e)(n, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      ...r
    },
    body: f
  }, l);
  if (_.status !== 200)
    return {
      success: !1,
      status: _.status,
      demonstratingProofOfPossessionNonce: null
    };
  const c = await _.json();
  let u = null;
  return _.headers.has(G) && (u = _.headers.get(G)), {
    success: !0,
    status: _.status,
    data: oe(c, o, i),
    demonstratingProofOfPossessionNonce: u
  };
}, en = (e, n) => async (t, s) => {
  s = s ? { ...s } : {};
  const o = ne(128), r = await Xe(o);
  await e.setCodeVerifierAsync(o), await e.setStateAsync(s.state), s.code_challenge = r, s.code_challenge_method = "S256";
  let i = "";
  if (s)
    for (const [l, a] of Object.entries(s))
      i === "" ? i += "?" : i += "&", i += `${l}=${encodeURIComponent(a)}`;
  n.open(`${t}${i}`);
}, G = "DPoP-Nonce", nn = (e) => async (n, t, s, o, r = 1e4) => {
  t = t ? { ...t } : {}, t.code_verifier = await e.getCodeVerifierAsync();
  const i = [];
  for (const c in t) {
    const u = encodeURIComponent(c), h = encodeURIComponent(t[c]);
    i.push(`${u}=${h}`);
  }
  const l = i.join("&"), a = await H(fetch)(n, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
      ...s
    },
    body: l
  }, r);
  if (await Promise.all([e.setCodeVerifierAsync(null), e.setStateAsync(null)]), a.status !== 200)
    return { success: !1, status: a.status };
  let f = null;
  a.headers.has(G) && (f = a.headers.get(G));
  const _ = await a.json();
  return {
    success: !0,
    data: {
      state: t.state,
      tokens: oe(_, null, o),
      demonstratingProofOfPossessionNonce: f
    }
  };
};
async function de(e, n, t) {
  const s = (l) => {
    e.tokens = l;
  }, { tokens: o, status: r } = await j(e)(0, n, t, s);
  return await C(e.configuration, e.configurationName) || await I(e.configurationName, e.configuration.storage).setTokens(e.tokens), e.tokens ? o : (await e.destroyAsync(r), null);
}
const tn = async (e, n) => {
  const t = await C(n, e.configurationName);
  if (t) {
    const s = await e.initAsync(n.authority, n.authority_configuration), { tokens: o } = await t.initAsync(s, "tryKeepExistingSessionAsync", n);
    return o;
  } else {
    const s = I(e.configurationName, n.storage ?? sessionStorage);
    let { tokens: o } = await s.initAsync();
    return o = z(o, e.tokens, n.token_renew_mode), o;
  }
};
async function Te(e, n = !1, t = null) {
  const s = e.configuration, o = `${s.client_id}_${e.configurationName}_${s.authority}`;
  let r;
  const i = await C(e.configuration, e.configurationName);
  return (s == null ? void 0 : s.storage) === (window == null ? void 0 : window.sessionStorage) && !i ? r = await de(e, n, t) : r = await navigator.locks.request(o, { ifAvailable: !0 }, async (l) => l ? await de(e, n, t) : (e.publishEvent(N.eventNames.syncTokensAsync_lock_not_available, { lock: "lock not available" }), await tn(e, s))), r ? (e.timeoutId && (e.timeoutId = J(e, e.tokens.expiresAt, t)), e.tokens) : null;
}
const J = (e, n, t = null) => {
  const s = e.configuration.refresh_time_before_tokens_expiration_in_second;
  return B.setTimeout(async () => {
    const r = { timeLeft: U(s, n) };
    e.publishEvent(N.eventNames.token_timer, r), await Te(e, !1, t);
  }, 1e3);
}, x = {
  FORCE_REFRESH: "FORCE_REFRESH",
  SESSION_LOST: "SESSION_LOST",
  NOT_CONNECTED: "NOT_CONNECTED",
  TOKENS_VALID: "TOKENS_VALID",
  TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_VALID: "TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_VALID",
  LOGOUT_FROM_ANOTHER_TAB: "LOGOUT_FROM_ANOTHER_TAB",
  REQUIRE_SYNC_TOKENS: "REQUIRE_SYNC_TOKENS"
}, sn = (e) => async (n, t, s, o = !1) => {
  const r = { nonce: null };
  if (!s)
    return { tokens: null, status: "NOT_CONNECTED", nonce: r };
  let i = r;
  const l = await e.initAsync(n.authority, n.authority_configuration), a = await C(n, t);
  if (a) {
    const { status: c, tokens: u } = await a.initAsync(l, "syncTokensAsync", n);
    if (c === "LOGGED_OUT")
      return { tokens: null, status: "LOGOUT_FROM_ANOTHER_TAB", nonce: r };
    if (c === "SESSIONS_LOST")
      return { tokens: null, status: "SESSIONS_LOST", nonce: r };
    if (!c || !u)
      return { tokens: null, status: "REQUIRE_SYNC_TOKENS", nonce: r };
    if (u.issuedAt !== s.issuedAt) {
      const g = U(n.refresh_time_before_tokens_expiration_in_second, u.expiresAt) > 0 ? "TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_VALID" : "TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_INVALID", m = await a.getNonceAsync();
      return { tokens: u, status: g, nonce: m };
    }
    i = await a.getNonceAsync();
  } else {
    const c = I(t, n.storage ?? sessionStorage);
    let { tokens: u, status: h } = await c.initAsync();
    if (u && (u = z(u, e.tokens, n.token_renew_mode)), u) {
      if (h === "SESSIONS_LOST")
        return { tokens: null, status: "SESSIONS_LOST", nonce: r };
      if (u.issuedAt !== s.issuedAt) {
        const m = U(n.refresh_time_before_tokens_expiration_in_second, u.expiresAt) > 0 ? "TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_VALID" : "TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_INVALID", d = await c.getNonceAsync();
        return { tokens: u, status: m, nonce: d };
      }
    } else
      return { tokens: null, status: "LOGOUT_FROM_ANOTHER_TAB", nonce: r };
    i = await c.getNonceAsync();
  }
  const _ = U(n.refresh_time_before_tokens_expiration_in_second, s.expiresAt) > 0 ? "TOKENS_VALID" : "TOKENS_INVALID";
  return o ? { tokens: s, status: "FORCE_REFRESH", nonce: i } : { tokens: s, status: _, nonce: i };
}, j = (e) => async (n = 0, t = !1, s = null, o) => {
  for (; !navigator.onLine && document.hidden; )
    await M({ milliseconds: 1e3 }), e.publishEvent(p.refreshTokensAsync, { message: "wait because navigator is offline and hidden" });
  let r = 6;
  for (; !navigator.onLine && r > 0; )
    await M({ milliseconds: 1e3 }), r--, e.publishEvent(p.refreshTokensAsync, { message: `wait because navigator is offline try ${r}` });
  const i = document.hidden, l = n + 1;
  s || (s = {});
  const a = e.configuration, f = (c, u = null, h = null) => ie(e.configurationName, e.configuration, e.publishEvent.bind(e))(c, u, h), _ = async () => {
    try {
      let c;
      const u = await C(a, e.configurationName);
      u ? c = u.getLoginParams() : c = I(e.configurationName, a.storage).getLoginParams();
      const h = await f({
        ...c.extras,
        ...s,
        prompt: "none"
      });
      return h ? h.error ? (o(null), e.publishEvent(p.refreshTokensAsync_error, { message: "refresh token silent" }), { tokens: null, status: "SESSION_LOST" }) : (o(h.tokens), e.publishEvent(N.eventNames.token_renewed, {}), { tokens: h.tokens, status: "LOGGED" }) : (o(null), e.publishEvent(p.refreshTokensAsync_error, { message: "refresh token silent not active" }), { tokens: null, status: "SESSION_LOST" });
    } catch (c) {
      return console.error(c), e.publishEvent(p.refreshTokensAsync_silent_error, { message: "exceptionSilent", exception: c.message }), await j(e)(l, t, s, o);
    }
  };
  if (n > 4)
    return i ? { tokens: e.tokens, status: "GIVE_UP" } : (o(null), e.publishEvent(p.refreshTokensAsync_error, { message: "refresh token" }), { tokens: null, status: "SESSION_LOST" });
  try {
    const { status: c, tokens: u, nonce: h } = await sn(e)(a, e.configurationName, e.tokens, t);
    switch (c) {
      case x.SESSION_LOST:
        return o(null), e.publishEvent(p.refreshTokensAsync_error, { message: "refresh token session lost" }), { tokens: null, status: "SESSION_LOST" };
      case x.NOT_CONNECTED:
        return o(null), { tokens: null, status: null };
      case x.TOKENS_VALID:
        return o(u), { tokens: u, status: "LOGGED_IN" };
      case x.TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_VALID:
        return o(u), e.publishEvent(N.eventNames.token_renewed, { reason: "TOKEN_UPDATED_BY_ANOTHER_TAB_TOKENS_VALID" }), { tokens: u, status: "LOGGED_IN" };
      case x.LOGOUT_FROM_ANOTHER_TAB:
        return o(null), e.publishEvent(p.logout_from_another_tab, { status: "session syncTokensAsync" }), { tokens: null, status: "LOGGED_OUT" };
      case x.REQUIRE_SYNC_TOKENS:
        return a.token_automatic_renew_mode == V.AutomaticOnlyWhenFetchExecuted && x.FORCE_REFRESH !== c ? (e.publishEvent(p.tokensInvalidAndWaitingActionsToRefresh, {}), { tokens: e.tokens, status: "GIVE_UP" }) : (e.publishEvent(p.refreshTokensAsync_begin, { tryNumber: n }), await _());
      default: {
        if (a.token_automatic_renew_mode == V.AutomaticOnlyWhenFetchExecuted && x.FORCE_REFRESH !== c)
          return e.publishEvent(p.tokensInvalidAndWaitingActionsToRefresh, {}), { tokens: e.tokens, status: "GIVE_UP" };
        if (e.publishEvent(p.refreshTokensAsync_begin, { refreshToken: u.refreshToken, status: c, tryNumber: n }), !u.refreshToken)
          return await _();
        const g = a.client_id, m = a.redirect_uri, d = a.authority, A = { ...a.token_request_extras ? a.token_request_extras : {} };
        for (const [T, y] of Object.entries(s))
          T.endsWith(":token_request") && (A[T.replace(":token_request", "")] = y);
        return await (async () => {
          const T = {
            client_id: g,
            redirect_uri: m,
            grant_type: "refresh_token",
            refresh_token: u.refreshToken
          }, y = await e.initAsync(d, a.authority_configuration), k = document.hidden ? 1e4 : 3e4 * 10, S = y.tokenEndpoint, P = {};
          a.demonstrating_proof_of_possession && (P.DPoP = await e.generateDemonstrationOfProofOfPossessionAsync(u.accessToken, S, "POST"));
          const b = await Ze(e.getFetch())(
            S,
            T,
            A,
            u,
            P,
            a.token_renew_mode,
            k
          );
          if (b.success) {
            const { isValid: D, reason: E } = ke(b.data, h.nonce, y);
            if (!D)
              return o(null), e.publishEvent(p.refreshTokensAsync_error, { message: `refresh token return not valid tokens, reason: ${E}` }), { tokens: null, status: "SESSION_LOST" };
            if (o(b.data), b.demonstratingProofOfPossessionNonce) {
              const L = await C(a, e.configurationName);
              L ? await L.setDemonstratingProofOfPossessionNonce(b.demonstratingProofOfPossessionNonce) : await I(e.configurationName, a.storage).setDemonstratingProofOfPossessionNonce(b.demonstratingProofOfPossessionNonce);
            }
            return e.publishEvent(p.refreshTokensAsync_end, { success: b.success }), e.publishEvent(N.eventNames.token_renewed, { reason: "REFRESH_TOKEN" }), { tokens: b.data, status: "LOGGED_IN" };
          } else
            return e.publishEvent(p.refreshTokensAsync_silent_error, {
              message: "bad request",
              tokenResponse: b
            }), b.status >= 400 && b.status < 500 ? (o(null), e.publishEvent(p.refreshTokensAsync_error, { message: `session lost: ${b.status}` }), { tokens: null, status: "SESSION_LOST" }) : await j(e)(l, t, s, o);
        })();
      }
    }
  } catch (c) {
    return console.error(c), e.publishEvent(p.refreshTokensAsync_silent_error, { message: "exception", exception: c.message }), j(e)(l, t, s, o);
  }
}, ie = (e, n, t) => (s = null, o = null, r = null) => {
  if (!n.silent_redirect_uri || !n.silent_login_uri)
    return Promise.resolve(null);
  try {
    t(p.silentLoginAsync_begin, {});
    let i = "";
    if (o && (s == null && (s = {}), s.state = o), r && (s == null && (s = {}), s.scope = r), s != null)
      for (const [c, u] of Object.entries(s))
        i === "" ? i = `?${encodeURIComponent(c)}=${encodeURIComponent(u)}` : i += `&${encodeURIComponent(c)}=${encodeURIComponent(u)}`;
    const l = n.silent_login_uri + i, a = l.indexOf("/", l.indexOf("//") + 2), f = l.substring(0, a), _ = document.createElement("iframe");
    return _.width = "0px", _.height = "0px", _.id = `${e}_oidc_iframe`, _.setAttribute("src", l), document.body.appendChild(_), new Promise((c, u) => {
      let h = !1;
      const g = () => {
        window.removeEventListener("message", m), _.remove(), h = !0;
      }, m = (d) => {
        if (d.origin === f && d.source === _.contentWindow) {
          const w = `${e}_oidc_tokens:`, A = `${e}_oidc_error:`, v = `${e}_oidc_exception:`, T = d.data;
          if (T && typeof T == "string" && !h) {
            if (T.startsWith(w)) {
              const y = JSON.parse(d.data.replace(w, ""));
              t(p.silentLoginAsync_end, {}), c(y), g();
            } else if (T.startsWith(A)) {
              const y = JSON.parse(d.data.replace(A, ""));
              t(p.silentLoginAsync_error, y), c({ error: "oidc_" + y.error, tokens: null, sessionState: null }), g();
            } else if (T.startsWith(v)) {
              const y = JSON.parse(d.data.replace(v, ""));
              t(p.silentLoginAsync_error, y), u(new Error(y.error)), g();
            }
          }
        }
      };
      try {
        window.addEventListener("message", m);
        const d = n.silent_login_timeout;
        setTimeout(() => {
          h || (g(), t(p.silentLoginAsync_error, { reason: "timeout" }), u(new Error("timeout")));
        }, d);
      } catch (d) {
        g(), t(p.silentLoginAsync_error, d), u(d);
      }
    });
  } catch (i) {
    throw t(p.silentLoginAsync_error, i), i;
  }
}, on = (e, n, t, s, o) => (r = null, i = void 0) => {
  r = { ...r };
  const l = (f, _, c) => ie(n, t, s.bind(o))(f, _, c);
  return (async () => {
    o.timeoutId && B.clearTimeout(o.timeoutId);
    let f;
    r && "state" in r && (f = r.state, delete r.state);
    try {
      const _ = t.extras ? { ...t.extras, ...r } : r, c = await l({
        ..._,
        prompt: "none"
      }, f, i);
      if (c)
        return o.tokens = c.tokens, s(p.token_aquired, {}), o.timeoutId = J(o, o.tokens.expiresAt, r), {};
    } catch (_) {
      return _;
    }
  })();
}, rn = (e, n, t) => (s, o, r, i = !1) => {
  const l = (a, f = void 0, _ = void 0) => ie(e.configurationName, t, e.publishEvent.bind(e))(a, f, _);
  return new Promise((a, f) => {
    if (t.silent_login_uri && t.silent_redirect_uri && t.monitor_session && s && r && !i) {
      const _ = () => {
        e.checkSessionIFrame.stop();
        const c = e.tokens;
        if (c === null)
          return;
        const u = c.idToken, h = c.idTokenPayload;
        return l({
          prompt: "none",
          id_token_hint: u,
          scope: t.scope || "openid"
        }).then((g) => {
          if (g.error)
            throw new Error(g.error);
          const m = g.tokens.idTokenPayload;
          if (h.sub === m.sub) {
            const d = g.sessionState;
            e.checkSessionIFrame.start(g.sessionState), h.sid === m.sid ? console.debug("SessionMonitor._callback: Same sub still logged in at OP, restarting check session iframe; session_state:", d) : console.debug("SessionMonitor._callback: Same sub still logged in at OP, session state has changed, restarting check session iframe; session_state:", d);
          } else
            console.debug("SessionMonitor._callback: Different subject signed into OP:", m.sub);
        }).catch(async (g) => {
          console.warn("SessionMonitor._callback: Silent login failed, logging out other tabs:", g);
          for (const [m, d] of Object.entries(n))
            await d.logoutOtherTabAsync(t.client_id, h.sub);
        });
      };
      e.checkSessionIFrame = new Oe(_, o, s), e.checkSessionIFrame.load().then(() => {
        e.checkSessionIFrame.start(r), a(e.checkSessionIFrame);
      }).catch((c) => {
        f(c);
      });
    } else
      a(null);
  });
}, be = (e) => {
  const n = e.match(
    // eslint-disable-next-line no-useless-escape
    /^([a-z][\w-]+\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/
  );
  if (!n)
    throw new Error("Invalid URL");
  let t = n[6], s = n[7];
  if (s) {
    const o = s.split("?");
    o.length === 2 && (s = o[0], t = o[1]);
  }
  return t.startsWith("?") && (t = t.slice(1)), n && {
    href: e,
    protocol: n[1],
    host: n[2],
    hostname: n[3],
    port: n[4],
    path: n[5],
    search: t,
    hash: s
  };
}, Y = (e) => {
  const n = be(e), { search: t } = n;
  return an(t);
}, an = (e) => {
  const n = {};
  let t, s, o;
  const r = e.split("&");
  for (s = 0, o = r.length; s < o; s++)
    t = r[s].split("="), n[decodeURIComponent(t[0])] = decodeURIComponent(t[1]);
  return n;
}, cn = (e, n, t, s, o) => (r = void 0, i = null, l = !1, a = void 0) => {
  const f = i;
  return i = { ...i }, (async () => {
    const c = r || o.getPath();
    if ("state" in i || (i.state = ne(16)), t(p.loginAsync_begin, {}), i)
      for (const u of Object.keys(i))
        u.endsWith(":token_request") && delete i[u];
    try {
      const u = l ? n.silent_redirect_uri : n.redirect_uri;
      a || (a = n.scope);
      const h = n.extras ? { ...n.extras, ...i } : i;
      h.nonce || (h.nonce = ne(12));
      const g = { nonce: h.nonce }, m = await C(n, e), d = await s(n.authority, n.authority_configuration);
      let w;
      if (m)
        m.setLoginParams({ callbackPath: c, extras: f }), await m.initAsync(d, "loginAsync", n), await m.setNonceAsync(g), m.startKeepAliveServiceWorker(), w = m;
      else {
        const v = I(e, n.storage ?? sessionStorage);
        v.setLoginParams({ callbackPath: c, extras: f }), await v.setNonceAsync(g), w = v;
      }
      const A = {
        client_id: n.client_id,
        redirect_uri: u,
        scope: a,
        response_type: "code",
        ...h
      };
      await en(w, o)(d.authorizationEndpoint, A);
    } catch (u) {
      throw t(p.loginAsync_error, u), u;
    }
  })();
}, ln = (e) => async (n = !1) => {
  try {
    e.publishEvent(p.loginCallbackAsync_begin, {});
    const t = e.configuration, s = t.client_id, o = n ? t.silent_redirect_uri : t.redirect_uri, r = t.authority, i = t.token_request_timeout, l = await e.initAsync(r, t.authority_configuration), a = e.location.getCurrentHref(), _ = Y(a).session_state, c = await C(t, e.configurationName);
    let u, h, g, m;
    if (c)
      await c.initAsync(l, "loginCallbackAsync", t), await c.setSessionStateAsync(_), h = await c.getNonceAsync(), g = c.getLoginParams(), m = await c.getStateAsync(), c.startKeepAliveServiceWorker(), u = c;
    else {
      const E = I(e.configurationName, t.storage ?? sessionStorage);
      await E.setSessionStateAsync(_), h = await E.getNonceAsync(), g = E.getLoginParams(), m = await E.getStateAsync(), u = E;
    }
    const d = Y(a);
    if (d.error || d.error_description)
      throw new Error(`Error from OIDC server: ${d.error} - ${d.error_description}`);
    if (d.iss && d.iss !== l.issuer)
      throw console.error(), new Error(`Issuer not valid (expected: ${l.issuer}, received: ${d.iss})`);
    if (d.state && d.state !== m)
      throw new Error(`State not valid (expected: ${m}, received: ${d.state})`);
    const w = {
      code: d.code,
      grant_type: "authorization_code",
      client_id: t.client_id,
      redirect_uri: o
    }, A = {};
    if (t.token_request_extras)
      for (const [E, L] of Object.entries(t.token_request_extras))
        A[E] = L;
    if (g && g.extras)
      for (const [E, L] of Object.entries(g.extras))
        E.endsWith(":token_request") && (A[E.replace(":token_request", "")] = L);
    const v = l.tokenEndpoint, T = {};
    if (t.demonstrating_proof_of_possession)
      if (c)
        T.DPoP = `DPOP_SECURED_BY_OIDC_SERVICE_WORKER_${e.configurationName}`;
      else {
        const E = await je(window)(t.demonstrating_proof_of_possession_configuration.generateKeyAlgorithm);
        await I(e.configurationName, t.storage).setDemonstratingProofOfPossessionJwkAsync(E), T.DPoP = await Ae(window)(t.demonstrating_proof_of_possession_configuration)(E, "POST", v);
      }
    const y = await nn(u)(
      v,
      { ...w, ...A },
      T,
      e.configuration.token_renew_mode,
      i
    );
    if (!y.success)
      throw new Error("Token request failed");
    let k;
    const S = y.data.tokens, P = y.data.demonstratingProofOfPossessionNonce;
    if (y.data.state !== A.state)
      throw new Error("state is not valid");
    const { isValid: b, reason: D } = ke(S, h.nonce, l);
    if (!b)
      throw new Error(`Tokens are not OpenID valid, reason: ${D}`);
    if (c) {
      if (S.refreshToken && !S.refreshToken.includes("SECURED_BY_OIDC_SERVICE_WORKER"))
        throw new Error("Refresh token should be hidden by service worker");
      if (P && S.accessToken && S.accessToken.includes("SECURED_BY_OIDC_SERVICE_WORKER"))
        throw new Error("Demonstration of proof of possession require Access token not hidden by service worker");
    }
    if (c)
      await c.initAsync(l, "syncTokensAsync", t), k = c.getLoginParams(), P && await c.setDemonstratingProofOfPossessionNonce(P);
    else {
      const E = I(e.configurationName, t.storage);
      k = E.getLoginParams(), P && await E.setDemonstratingProofOfPossessionNonce(P);
    }
    return await e.startCheckSessionAsync(l.checkSessionIframe, s, _, n), t.preload_user_info && await e.userInfoAsync(), e.publishEvent(p.loginCallbackAsync_end, {}), {
      tokens: S,
      state: "request.state",
      callbackPath: k.callbackPath
    };
  } catch (t) {
    throw console.error(t), e.publishEvent(p.loginCallbackAsync_error, t), t;
  }
}, he = {
  access_token: "access_token",
  refresh_token: "refresh_token"
}, Q = (e, n) => {
  const t = {};
  if (e) {
    for (const [s, o] of Object.entries(e))
      if (s.endsWith(n)) {
        const r = s.replace(n, "");
        t[r] = o;
      }
    return t;
  }
  return t;
}, un = (e) => {
  const n = {};
  if (e) {
    for (const [t, s] of Object.entries(e))
      t.includes(":") || (n[t] = s);
    return n;
  }
  return n;
}, _n = (e) => async (n) => {
  B.clearTimeout(e.timeoutId), e.timeoutId = null, e.checkSessionIFrame && e.checkSessionIFrame.stop();
  const t = await C(e.configuration, e.configurationName);
  t ? await t.clearAsync(n) : await I(e.configurationName, e.configuration.storage).clearAsync(n), e.tokens = null, e.userInfo = null;
}, fn = (e, n, t, s, o) => async (r = void 0, i = null) => {
  const l = e.configuration, a = await e.initAsync(l.authority, l.authority_configuration);
  r && typeof r != "string" && (r = void 0, s.warn("callbackPathOrUrl path is not a string"));
  const f = r ?? o.getPath();
  let _ = !1;
  r && (_ = r.includes("https://") || r.includes("http://"));
  const c = _ ? r : o.getOrigin() + f, u = e.tokens ? e.tokens.idToken : "";
  try {
    const w = a.revocationEndpoint;
    if (w) {
      const A = [], v = e.tokens ? e.tokens.accessToken : null;
      if (v && l.logout_tokens_to_invalidate.includes(he.access_token)) {
        const y = Q(i, ":revoke_access_token"), k = fe(t)(
          w,
          v,
          te.access_token,
          l.client_id,
          y
        );
        A.push(k);
      }
      const T = e.tokens ? e.tokens.refreshToken : null;
      if (T && l.logout_tokens_to_invalidate.includes(he.refresh_token)) {
        const y = Q(i, ":revoke_refresh_token"), k = fe(t)(
          w,
          T,
          te.refresh_token,
          l.client_id,
          y
        );
        A.push(k);
      }
      A.length > 0 && await Promise.all(A);
    }
  } catch (w) {
    s.warn("logoutAsync: error when revoking tokens, if the error persist, you ay configure property logout_tokens_to_invalidate from configuration to avoid this error"), s.warn(w);
  }
  const h = e.tokens && e.tokens.idTokenPayload ? e.tokens.idTokenPayload.sub : null;
  await e.destroyAsync("LOGGED_OUT");
  for (const [w, A] of Object.entries(n))
    A !== e ? await e.logoutSameTabAsync(e.configuration.client_id, h) : e.publishEvent(p.logout_from_same_tab, {});
  const g = Q(i, ":oidc");
  if (g && g.no_reload === "true")
    return;
  const d = un(i);
  if (a.endSessionEndpoint) {
    "id_token_hint" in d || (d.id_token_hint = u), !("post_logout_redirect_uri" in d) && r !== null && (d.post_logout_redirect_uri = c);
    let w = "";
    for (const [A, v] of Object.entries(d))
      v != null && (w === "" ? w += "?" : w += "&", w += `${A}=${encodeURIComponent(v)}`);
    o.open(`${a.endSessionEndpoint}${w}`);
  } else
    o.reload();
}, Ee = (e, n, t = !1) => async (...s) => {
  var h;
  const [o, r, ...i] = s, l = r ? { ...r } : { method: "GET" };
  let a = new Headers();
  l.headers && (a = l.headers instanceof Headers ? l.headers : new Headers(l.headers));
  const f = n, _ = await ge(f), c = (h = _ == null ? void 0 : _.tokens) == null ? void 0 : h.accessToken;
  if (a.has("Accept") || a.set("Accept", "application/json"), c) {
    if (f.configuration.demonstrating_proof_of_possession && t) {
      const g = await f.generateDemonstrationOfProofOfPossessionAsync(c, o.toString(), l.method);
      a.set("Authorization", `PoP ${c}`), a.set("DPoP", g);
    } else
      a.set("Authorization", `Bearer ${c}`);
    l.credentials || (l.credentials = "same-origin");
  }
  const u = { ...l, headers: a };
  return await e(o, u, ...i);
}, dn = (e) => async (n = !1, t = !1) => {
  if (e.userInfo != null && !n)
    return e.userInfo;
  const s = e.configuration, r = (await e.initAsync(s.authority, s.authority_configuration)).userInfoEndpoint, l = await (async () => {
    const f = await Ee(fetch, e, t)(r);
    return f.status !== 200 ? null : f.json();
  })();
  return e.userInfo = l, l;
};
class X {
  open(n) {
    window.location.href = n;
  }
  reload() {
    window.location.reload();
  }
  getCurrentHref() {
    return window.location.href;
  }
  getPath() {
    const n = window.location;
    return n.pathname + (n.search || "") + (n.hash || "");
  }
  getOrigin() {
    return window.origin;
  }
}
const hn = (e) => !!(e.os === "iOS" && e.osVersion.startsWith("12") || e.os === "Mac OS X" && e.osVersion.startsWith("10_15_6")), yn = (e) => {
  const n = e.appVersion, t = e.userAgent, s = "-";
  let o = s;
  const r = [
    { s: "Windows 10", r: /(Windows 10.0|Windows NT 10.0)/ },
    { s: "Windows 8.1", r: /(Windows 8.1|Windows NT 6.3)/ },
    { s: "Windows 8", r: /(Windows 8|Windows NT 6.2)/ },
    { s: "Windows 7", r: /(Windows 7|Windows NT 6.1)/ },
    { s: "Windows Vista", r: /Windows NT 6.0/ },
    { s: "Windows Server 2003", r: /Windows NT 5.2/ },
    { s: "Windows XP", r: /(Windows NT 5.1|Windows XP)/ },
    { s: "Windows 2000", r: /(Windows NT 5.0|Windows 2000)/ },
    { s: "Windows ME", r: /(Win 9x 4.90|Windows ME)/ },
    { s: "Windows 98", r: /(Windows 98|Win98)/ },
    { s: "Windows 95", r: /(Windows 95|Win95|Windows_95)/ },
    { s: "Windows NT 4.0", r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
    { s: "Windows CE", r: /Windows CE/ },
    { s: "Windows 3.11", r: /Win16/ },
    { s: "Android", r: /Android/ },
    { s: "Open BSD", r: /OpenBSD/ },
    { s: "Sun OS", r: /SunOS/ },
    { s: "Chrome OS", r: /CrOS/ },
    { s: "Linux", r: /(Linux|X11(?!.*CrOS))/ },
    { s: "iOS", r: /(iPhone|iPad|iPod)/ },
    { s: "Mac OS X", r: /Mac OS X/ },
    { s: "Mac OS", r: /(Mac OS|MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
    { s: "QNX", r: /QNX/ },
    { s: "UNIX", r: /UNIX/ },
    { s: "BeOS", r: /BeOS/ },
    { s: "OS/2", r: /OS\/2/ },
    { s: "Search Bot", r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/ }
  ];
  for (const l in r) {
    const a = r[l];
    if (a.r.test(t)) {
      o = a.s;
      break;
    }
  }
  let i = s;
  switch (/Windows/.test(o) && (i = /Windows (.*)/.exec(o)[1], o = "Windows"), o) {
    case "Mac OS":
    case "Mac OS X":
    case "Android":
      i = /(?:Android|Mac OS|Mac OS X|MacPPC|MacIntel|Mac_PowerPC|Macintosh) ([._\d]+)/.exec(t)[1];
      break;
    case "iOS": {
      const l = /OS (\d+)_(\d+)_?(\d+)?/.exec(n);
      l != null && l.length > 2 && (i = l[1] + "." + l[2] + "." + (parseInt(l[3]) | 0));
      break;
    }
  }
  return {
    os: o,
    osVersion: i
  };
};
function gn() {
  const e = navigator.userAgent;
  let n, t = e.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  if (/trident/i.test(t[1]))
    return n = /\brv[ :]+(\d+)/g.exec(e) || [], { name: "ie", version: n[1] || "" };
  if (t[1] === "Chrome" && (n = e.match(/\bOPR|Edge\/(\d+)/), n != null)) {
    let s = n[1];
    if (!s) {
      const o = e.split(n[0] + "/");
      o.length > 1 && (s = o[1]);
    }
    return { name: "opera", version: s };
  }
  return t = t[2] ? [t[1], t[2]] : [navigator.appName, navigator.appVersion, "-?"], (n = e.match(/version\/(\d+)/i)) != null && t.splice(1, 1, n[1]), {
    name: t[0].toLowerCase(),
    version: t[1]
  };
}
const kn = () => {
  const { name: e, version: n } = gn();
  if (e === "chrome" && parseInt(n) <= 70 || e === "opera" && (!n || parseInt(n.split(".")[0]) < 80) || e === "ie")
    return !1;
  const t = yn(navigator);
  return !hn(t);
}, mn = async (e) => {
  let n;
  if (e.tokens != null)
    return !1;
  e.publishEvent(p.tryKeepExistingSessionAsync_begin, {});
  try {
    const t = e.configuration, s = await e.initAsync(t.authority, t.authority_configuration);
    if (n = await C(t, e.configurationName), n) {
      const { tokens: o } = await n.initAsync(s, "tryKeepExistingSessionAsync", t);
      if (o) {
        n.startKeepAliveServiceWorker(), e.tokens = o;
        const r = n.getLoginParams(e.configurationName);
        e.timeoutId = J(e, e.tokens.expiresAt, r.extras);
        const i = await n.getSessionStateAsync();
        return await e.startCheckSessionAsync(s.check_session_iframe, t.client_id, i), t.preload_user_info && await e.userInfoAsync(), e.publishEvent(p.tryKeepExistingSessionAsync_end, {
          success: !0,
          message: "tokens inside ServiceWorker are valid"
        }), !0;
      }
      e.publishEvent(p.tryKeepExistingSessionAsync_end, {
        success: !1,
        message: "no exiting session found"
      });
    } else {
      t.service_worker_relative_url && e.publishEvent(p.service_worker_not_supported_by_browser, {
        message: "service worker is not supported by this browser"
      });
      const o = I(e.configurationName, t.storage ?? sessionStorage), { tokens: r } = await o.initAsync();
      if (r) {
        e.tokens = z(r, null, t.token_renew_mode);
        const i = o.getLoginParams();
        e.timeoutId = J(e, e.tokens.expiresAt, i.extras);
        const l = await o.getSessionStateAsync();
        return await e.startCheckSessionAsync(s.check_session_iframe, t.client_id, l), t.preload_user_info && await e.userInfoAsync(), e.publishEvent(p.tryKeepExistingSessionAsync_end, {
          success: !0,
          message: "tokens inside storage are valid"
        }), !0;
      }
    }
    return e.publishEvent(p.tryKeepExistingSessionAsync_end, {
      success: !1,
      message: n ? "service worker sessions not retrieved" : "session storage sessions not retrieved"
    }), !1;
  } catch (t) {
    return console.error(t), n && await n.clearAsync(), e.publishEvent(p.tryKeepExistingSessionAsync_error, "tokens inside ServiceWorker are invalid"), !1;
  }
}, pn = () => fetch;
class se {
  constructor(n) {
    this.authorizationEndpoint = n.authorization_endpoint, this.tokenEndpoint = n.token_endpoint, this.revocationEndpoint = n.revocation_endpoint, this.userInfoEndpoint = n.userinfo_endpoint, this.checkSessionIframe = n.check_session_iframe, this.issuer = n.issuer, this.endSessionEndpoint = n.end_session_endpoint;
  }
}
const W = {}, wn = (e, n = new X()) => (t, s = "default") => (W[s] || (W[s] = new N(t, s, e, n)), W[s]), An = async (e) => {
  const { parsedTokens: n, callbackPath: t } = await e.loginCallbackAsync();
  return e.timeoutId = J(e, n.expiresAt), { callbackPath: t };
}, Sn = (e) => Math.floor(Math.random() * e), F = class F {
  constructor(n, t = "default", s, o = new X()) {
    this.initPromise = null, this.tryKeepExistingSessionPromise = null, this.loginPromise = null, this.loginCallbackPromise = null, this.loginCallbackWithAutoTokensRenewPromise = null, this.userInfoPromise = null, this.renewTokensPromise = null, this.logoutPromise = null;
    let r = n.silent_login_uri;
    n.silent_redirect_uri && !n.silent_login_uri && (r = `${n.silent_redirect_uri.replace("-callback", "").replace("callback", "")}-login`);
    let i = n.refresh_time_before_tokens_expiration_in_second ?? 120;
    i > 60 && (i = i - Math.floor(Math.random() * 40)), this.location = o ?? new X();
    const l = n.service_worker_update_require_callback ?? Le(this.location);
    this.configuration = {
      ...n,
      silent_login_uri: r,
      token_automatic_renew_mode: n.token_automatic_renew_mode ?? V.AutomaticBeforeTokenExpiration,
      monitor_session: n.monitor_session ?? !1,
      refresh_time_before_tokens_expiration_in_second: i,
      silent_login_timeout: n.silent_login_timeout ?? 12e3,
      token_renew_mode: n.token_renew_mode ?? Z.access_token_or_id_token_invalid,
      demonstrating_proof_of_possession: n.demonstrating_proof_of_possession ?? !1,
      authority_timeout_wellknowurl_in_millisecond: n.authority_timeout_wellknowurl_in_millisecond ?? 1e4,
      logout_tokens_to_invalidate: n.logout_tokens_to_invalidate ?? ["access_token", "refresh_token"],
      service_worker_update_require_callback: l,
      service_worker_activate: n.service_worker_activate ?? kn,
      demonstrating_proof_of_possession_configuration: n.demonstrating_proof_of_possession_configuration ?? Ke,
      preload_user_info: n.preload_user_info ?? !1
    }, this.getFetch = s ?? pn, this.configurationName = t, this.tokens = null, this.userInfo = null, this.events = [], this.timeoutId = null, this.loginCallbackWithAutoTokensRenewAsync.bind(this), this.initAsync.bind(this), this.loginCallbackAsync.bind(this), this.subscribeEvents.bind(this), this.removeEventSubscription.bind(this), this.publishEvent.bind(this), this.destroyAsync.bind(this), this.logoutAsync.bind(this), this.renewTokensAsync.bind(this), this.initAsync(this.configuration.authority, this.configuration.authority_configuration);
  }
  subscribeEvents(n) {
    const t = Sn(9999999999999).toString();
    return this.events.push({ id: t, func: n }), t;
  }
  removeEventSubscription(n) {
    const t = this.events.filter((s) => s.id !== n);
    this.events = t;
  }
  publishEvent(n, t) {
    this.events.forEach((s) => {
      s.func(n, t);
    });
  }
  static get(n = "default") {
    const t = typeof process > "u";
    if (!Object.prototype.hasOwnProperty.call(W, n) && t)
      throw Error(`OIDC library does seem initialized.
Please checkout that you are using OIDC hook inside a <OidcProvider configurationName="${n}"></OidcProvider> component.`);
    return W[n];
  }
  _silentLoginCallbackFromIFrame() {
    if (this.configuration.silent_redirect_uri && this.configuration.silent_login_uri) {
      const n = this.location, t = Y(n.getCurrentHref());
      window.parent.postMessage(`${this.configurationName}_oidc_tokens:${JSON.stringify({ tokens: this.tokens, sessionState: t.session_state })}`, n.getOrigin());
    }
  }
  _silentLoginErrorCallbackFromIFrame(n = null) {
    if (this.configuration.silent_redirect_uri && this.configuration.silent_login_uri) {
      const t = this.location, s = Y(t.getCurrentHref());
      s.error ? window.parent.postMessage(`${this.configurationName}_oidc_error:${JSON.stringify({ error: s.error })}`, t.getOrigin()) : window.parent.postMessage(`${this.configurationName}_oidc_exception:${JSON.stringify({ error: n == null ? "" : n.toString() })}`, t.getOrigin());
    }
  }
  async silentLoginCallbackAsync() {
    try {
      await this.loginCallbackAsync(!0), this._silentLoginCallbackFromIFrame();
    } catch (n) {
      console.error(n), this._silentLoginErrorCallbackFromIFrame(n);
    }
  }
  async initAsync(n, t) {
    if (this.initPromise !== null)
      return this.initPromise;
    const s = async () => {
      if (t != null)
        return new se({
          authorization_endpoint: t.authorization_endpoint,
          end_session_endpoint: t.end_session_endpoint,
          revocation_endpoint: t.revocation_endpoint,
          token_endpoint: t.token_endpoint,
          userinfo_endpoint: t.userinfo_endpoint,
          check_session_iframe: t.check_session_iframe,
          issuer: t.issuer
        });
      const r = await C(this.configuration, this.configurationName) ? window.localStorage : null;
      return await Qe(this.getFetch())(n, this.configuration.authority_time_cache_wellknowurl_in_second ?? 60 * 60, r, this.configuration.authority_timeout_wellknowurl_in_millisecond);
    };
    return this.initPromise = s(), this.initPromise.then((o) => (this.initPromise = null, o));
  }
  async tryKeepExistingSessionAsync() {
    return this.tryKeepExistingSessionPromise !== null ? this.tryKeepExistingSessionPromise : (this.tryKeepExistingSessionPromise = mn(this), this.tryKeepExistingSessionPromise.then((n) => (this.tryKeepExistingSessionPromise = null, n)));
  }
  async startCheckSessionAsync(n, t, s, o = !1) {
    await rn(this, W, this.configuration)(n, t, s, o);
  }
  async loginAsync(n = void 0, t = null, s = !1, o = void 0, r = !1) {
    return this.loginPromise !== null ? this.loginPromise : r ? on(window, this.configurationName, this.configuration, this.publishEvent.bind(this), this)(t, o) : (this.loginPromise = cn(this.configurationName, this.configuration, this.publishEvent.bind(this), this.initAsync.bind(this), this.location)(n, t, s, o), this.loginPromise.then((i) => (this.loginPromise = null, i)));
  }
  async loginCallbackAsync(n = !1) {
    if (this.loginCallbackPromise !== null)
      return this.loginCallbackPromise;
    const t = async () => {
      const s = await ln(this)(n), o = s.tokens;
      return this.tokens = o, await C(this.configuration, this.configurationName) || I(this.configurationName, this.configuration.storage).setTokens(o), this.publishEvent(F.eventNames.token_aquired, o), { parsedTokens: o, state: s.state, callbackPath: s.callbackPath };
    };
    return this.loginCallbackPromise = t(), this.loginCallbackPromise.then((s) => (this.loginCallbackPromise = null, s));
  }
  async generateDemonstrationOfProofOfPossessionAsync(n, t, s, o = {}) {
    const r = this.configuration, i = {
      ath: await ve(n),
      ...o
    }, l = await C(r, this.configurationName);
    let a;
    if (l)
      return `DPOP_SECURED_BY_OIDC_SERVICE_WORKER_${this.configurationName}`;
    const f = I(this.configurationName, r.storage);
    let _ = await f.getDemonstratingProofOfPossessionJwkAsync();
    return a = await f.getDemonstratingProofOfPossessionNonce(), a && (i.nonce = a), await Ae(window)(r.demonstrating_proof_of_possession_configuration)(_, s, t, i);
  }
  loginCallbackWithAutoTokensRenewAsync() {
    return this.loginCallbackWithAutoTokensRenewPromise !== null ? this.loginCallbackWithAutoTokensRenewPromise : (this.loginCallbackWithAutoTokensRenewPromise = An(this), this.loginCallbackWithAutoTokensRenewPromise.then((n) => (this.loginCallbackWithAutoTokensRenewPromise = null, n)));
  }
  userInfoAsync(n = !1, t = !1) {
    return this.userInfoPromise !== null ? this.userInfoPromise : (this.userInfoPromise = dn(this)(n, t), this.userInfoPromise.then((s) => (this.userInfoPromise = null, s)));
  }
  async renewTokensAsync(n = null) {
    if (this.renewTokensPromise !== null)
      return this.renewTokensPromise;
    if (this.timeoutId)
      return B.clearTimeout(this.timeoutId), this.renewTokensPromise = Te(this, !0, n), this.renewTokensPromise.then((t) => (this.renewTokensPromise = null, t));
  }
  async destroyAsync(n) {
    return await _n(this)(n);
  }
  async logoutSameTabAsync(n, t) {
    this.configuration.monitor_session && this.configuration.client_id === n && t && this.tokens && this.tokens.idTokenPayload && this.tokens.idTokenPayload.sub === t && (await this.destroyAsync("LOGGED_OUT"), this.publishEvent(p.logout_from_same_tab, { mmessage: "SessionMonitor", sub: t }));
  }
  async logoutOtherTabAsync(n, t) {
    this.configuration.monitor_session && this.configuration.client_id === n && t && this.tokens && this.tokens.idTokenPayload && this.tokens.idTokenPayload.sub === t && (await this.destroyAsync("LOGGED_OUT"), this.publishEvent(p.logout_from_another_tab, { message: "SessionMonitor", sub: t }));
  }
  async logoutAsync(n = void 0, t = null) {
    return this.logoutPromise ? this.logoutPromise : (this.logoutPromise = fn(this, W, this.getFetch(), console, this.location)(n, t), this.logoutPromise.then((s) => (this.logoutPromise = null, s)));
  }
};
F.getOrCreate = (n, t) => (s, o = "default") => wn(n, t)(s, o), F.eventNames = p;
let N = F;
const K = class K {
  constructor(n) {
    this._oidc = n;
  }
  subscribeEvents(n) {
    return this._oidc.subscribeEvents(n);
  }
  removeEventSubscription(n) {
    this._oidc.removeEventSubscription(n);
  }
  publishEvent(n, t) {
    this._oidc.publishEvent(n, t);
  }
  static get(n = "default") {
    return new K(N.get(n));
  }
  tryKeepExistingSessionAsync() {
    return this._oidc.tryKeepExistingSessionAsync();
  }
  loginAsync(n = void 0, t = null, s = !1, o = void 0, r = !1) {
    return this._oidc.loginAsync(n, t, s, o, r);
  }
  logoutAsync(n = void 0, t = null) {
    return this._oidc.logoutAsync(n, t);
  }
  silentLoginCallbackAsync() {
    return this._oidc.silentLoginCallbackAsync();
  }
  renewTokensAsync(n = null) {
    return this._oidc.renewTokensAsync(n);
  }
  loginCallbackAsync() {
    return this._oidc.loginCallbackWithAutoTokensRenewAsync();
  }
  get tokens() {
    return this._oidc.tokens;
  }
  get configuration() {
    return this._oidc.configuration;
  }
  async generateDemonstrationOfProofOfPossessionAsync(n, t, s, o = {}) {
    return this._oidc.generateDemonstrationOfProofOfPossessionAsync(n, t, s, o);
  }
  async getValidTokenAsync(n = 200, t = 50) {
    return ge(this._oidc, n, t);
  }
  fetchWithTokens(n, t) {
    return Ee(n, this, t);
  }
  async userInfoAsync(n = !1, t = !1) {
    return this._oidc.userInfoAsync(n, t);
  }
  userInfo() {
    return this._oidc.userInfo;
  }
};
K.getOrCreate = (n, t = new X()) => (s, o = "default") => new K(N.getOrCreate(n, t)(s, o)), K.eventNames = N.eventNames;
let ye = K;

const useNoAuthenticatedRequest = (endpoint = 'plateform') => {
    return useMemo(() => ({
        url: endpoint === 'fs'
            ? fsConfig().url
            : endpoint === 'im'
                ? imConfig().url
                : platFormConfig().url
    }), []);
};
const useAuthenticatedRequest = (endpoint = 'plateform') => {
    const auth = useAuth();
    return useMemo(() => ({
        url: endpoint === 'fs'
            ? fsConfig().url
            : endpoint === 'im'
                ? imConfig().url
                : platFormConfig().url,
        jwt: auth.keycloak.token
    }), [auth.keycloak.token]);
};

const imConfig = () => {
    var _a, _b;
    // @ts-ignore
    return (_b = (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.im;
};
const fsConfig = () => {
    var _a, _b;
    // @ts-ignore
    return (_b = (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.fs;
};
const platFormConfig = () => {
    var _a, _b;
    // @ts-ignore
    return (_b = (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.platform;
};
const G2ConfigBuilder = (config) => {
    var _a;
    // @ts-ignore
    window._env_ = Object.assign(Object.assign({}, window._env_), { config: Object.assign(Object.assign({}, (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config), config) });
};
const g2Config = () => {
    var _a;
    // @ts-ignore
    return (_a = window._env_) === null || _a === void 0 ? void 0 : _a.config;
};

const LoadingProviders = () => {
    const theme = useTheme();
    return (React__default.createElement(Loading, { icon: theme.logoUrl ? (React__default.createElement("img", { src: theme.logoUrl, style: {
                width: '80vw',
                maxWidth: '250px',
                maxHeight: '80vh',
                marginBottom: '20px'
            }, alt: 'The application logo' })) : (React__default.createElement(Box, { sx: {
                width: '80vw',
                maxWidth: '250px',
                height: '80vh',
                maxHeight: '250px'
            } })) }));
};

const KeycloakProvider = (props) => {
    const { children, configuration } = props, others = __rest(props, ["children", "configuration"]);
    const [defaultConfiguration] = useState({
        client_id: g2Config().keycloak.clientId,
        redirect_uri: window.location.origin + '/authentication/callback',
        silent_redirect_uri: window.location.origin + '/authentication/silent-callback',
        scope: 'openid',
        authority: g2Config().keycloak.url + '/realms/' + g2Config().keycloak.realm
        // service_worker_relative_url:'/OidcServiceWorker.js',
        // service_worker_only:true,
    });
    return (React__default.createElement(OidcProvider, Object.assign({ configuration: configuration !== null && configuration !== void 0 ? configuration : defaultConfiguration, loadingComponent: LoadingProviders, authenticatingComponent: LoadingProviders, callbackSuccessComponent: LoadingProviders, sessionLostComponent: SessionLostComponent }, others), children));
};
const SessionLostComponent = () => {
    useEffect(() => {
        var _a;
        (_a = ye.get('default')) === null || _a === void 0 ? void 0 : _a.loginAsync();
    }, []);
    return React__default.createElement(React__default.Fragment, null);
};

const G2Translations = {
    fr: {
        g2: {
            fieldRequired: 'Ce champ est obligatoire',
            fileRequired: 'Vous devez insérer ce fichier: {{fileDesc}}',
            needFieldAbove0: 'La valeur doit être supérieure à 0',
            needFieldPositive: 'La valeur doit être supérieure ou égale à 0',
            phoneNeedsToHave10Digits: 'Le numéro de téléphone doit contenir dix chiffres',
            incorrectEmail: "L'email renseigné n'est pas correct",
            requiredStreet: "Vous devez renseigner l'addresse en plus de la ville et du code postal",
            requiredPostalCode: 'Vous devez renseigner le code postal pour avoir une adresse complète',
            requiredCity: 'Vous devez renseigner la ville pour avoir une adresse complète',
            emailAlreadyUsed: 'Cet email est déjà utilisé',
            completeTheEmail: 'Vous devez renseigner le mail',
            enterAValidEmail: "L'email renseigné n'est pas correct",
            siretNotFound: 'Aucune information trouvé. Saisissez les informations ci-dessous manuellement',
            siretDescription: 'Le numéro de siret permettra de remplir automatiquement une partie des champs suivants',
            addPicture: 'Ajouter une image',
            removePicture: "Retirer l'image",
            'file-invalid-type': 'Les fichiers doivent être au format: {{formats}}',
            'file-too-large': 'Les fichiers ne devrait pas dépasser {{sizeLimit}}Mo',
            'too-many-files': "Vous ne pouvez ajouter qu'un seul fichier à la fois",
            or: 'ou',
            Required: 'Obligatoire',
            openFullScreen: 'Ouvrir en pleine écran',
            canDrag: 'Vous pouvez déplacer ce point',
            addMarker: 'Ajouter un marker',
            useMyPosition: 'Utiliser ma position',
            logo: "Logo de l'entreprise",
            siret: 'Numéro de siret',
            name: 'Nom',
            roles: 'Rôle',
            facultativeField: '{{label}} (facultatif)',
            website: 'Site web',
            description: 'Description',
            organization: 'Organisation',
            address: 'Adresse',
            postalCode: 'Code postal',
            city: 'Ville',
            givenName: 'Prénom',
            familyName: 'Nom de famille',
            memberOf: '$t(g2.organization)',
            email: 'Adresse mail',
            phone: 'Numéro de téléphone',
            sendVerifyEmail: "Envoyer le mail de vérification de l'adresse email",
            sendResetPassword: "Envoyer le mail de création de son mot de passe à l'utilisateur",
            user: 'Utilisateur',
            completeThePassword: 'Vous devez renseigner le mot de passe',
            passWordLongerThan8: "Le mot de passe doit être composé d'au minimum 8 caractères",
            samePasswordCheck: 'Vous devez renseigner un mot de passe identique au premier',
            newPassword: 'Nouveau mot de passe',
            passwordCheck: 'Vérification du mot de passe',
            cancel: 'Annuler',
            confirm: 'Confirmer',
            password: 'Mot de passe',
            changePassword: 'Changer le mot de passe',
            passwordChangement: 'Changement de mot de passe',
            passwordEmail: "Un email pour effectuer le changement de mot de passe va être envoyé à l'adresse email de votre compte.",
            clear: 'Vider',
            accept: 'Valider',
            today: "Aujourd'hui",
            addImages: 'Ajouter une ou plusieurs images',
            gallery: 'Galerie',
            save: 'Enregistrer',
            copySuccess: 'Déjà copié, cliquez ici pour re-copier',
            copyToClipboard: 'Copier dans le presse papier',
            required: 'Obligatoire',
            noData: 'Aucune donnée trouvé',
            noOrganization: "Vous n'êtes pas inclut dans une organisation",
            totalItem: '{{start}} - {{end}} sur {{total}}',
            toFilter: 'Filtrer',
            toFilterCount: 'Filtrer ({{count}})',
            apply: 'Appliquer',
            clearFilters: 'Retirer les filtres',
            title: 'Titre',
            text: 'Texte'
        },
        http: {
            '600': "{{errorMessage}} à cause d'un problème de connexion",
            '401': "Vous essayez d'accéder à une ressource à laquelle vous n'êtes pas autorisé",
            '500': "{{errorMessage}} à cause d'un problème technique, si ce problème persiste contactez un administrateur",
            errors: {
                organizationPage: "La liste des organisations n'a pas pu être récupérée",
                organizationGet: "Le détail de l'organisation n'a pas pu être répuré",
                organizationUpdate: "L'organisation n'a pas pu être mise à jour",
                organizationCreate: "L'organisation n'a pas pu être créée",
                organizationUploadLogo: "Le logo de l'organisation n'a pu être envoyé",
                organizationDisable: "L'organisation n'a pas pu être supprimée",
                userPage: "La liste des utilisateurs n'a pas pu être récupérée",
                userGet: "le détail de l'utlisateur n'a pas pu être répuré",
                userUpdate: "L'utilisateur n'a pas pu être mis à jour",
                userCreate: "L'utilisateur n'a pas pu être créé",
                userUpdatePassword: "Le mot de passe de l'utlisateur n'a pas pu être mis à jour",
                userResetPassword: "La demande de renouvellement de mot de passe de l'utlisateur n'a pas pu être envoyée",
                userUpdateEmail: "L'email de l'utilisateur n'a pas pu être mis à jour",
                userDisable: "L'utilisateur n'a pas pu être supprimé"
            },
            success: {
                organizationUpdate: "L'organisation a bien été mise à jour",
                organizationCreate: "L'organisation a bien été créée",
                organizationDisable: "L'organisation a bien été supprimée",
                userUpdate: "L'utilisateur a bien été mis à jour",
                userCreate: "L'utilisateur a bien été créé",
                userUpdatePassword: "Le mot de passe de l'utlisateur a bien été mis à jour",
                userResetPassword: "La demande de renouvellement de mot de passe de l'utlisateur a bien été envoyée",
                userDisable: "L'utilisateur a bien été supprimé"
            }
        }
    },
    en: {
        g2: {
            fieldRequired: 'This field is required',
            fileRequired: 'You must insert this file: {{fileDesc}}',
            needFieldAbove0: 'The value must be greater than 0',
            needFieldPositive: 'The value must be greater than or equal to 0',
            phoneNeedsToHave10Digits: 'The phone number must have ten digits',
            incorrectEmail: 'The entered email is incorrect',
            requiredStreet: 'You must provide the address in addition to the city and postal code',
            requiredPostalCode: 'You must provide the postal code for a complete address',
            requiredCity: 'You must provide the city for a complete address',
            emailAlreadyUsed: 'This email is already in use',
            completeTheEmail: 'You must complete the email',
            enterAValidEmail: 'The entered email is incorrect',
            siretNotFound: 'No information found. Please manually enter the following information',
            siretDescription: 'The SIRET number will automatically populate some of the following fields',
            addPicture: 'Add a picture',
            removePicture: 'Remove the picture',
            'file-invalid-type': 'Your file must be in the following format: {{formats}}',
            'file-too-large': 'Your file should not exceed {{sizeLimit}}MB',
            'too-many-files': 'You can only add one file at a time',
            or: 'or',
            Required: 'Required',
            openFullScreen: 'Open in fullscreen',
            canDrag: 'You can move this point',
            addMarker: 'Add a marker',
            useMyPosition: 'Use my position',
            logo: 'Company Logo',
            siret: 'SIRET Number',
            name: 'Name',
            roles: 'Roles',
            facultativeField: '{{label}} (optional)',
            website: 'Website',
            description: 'Description',
            organization: 'Organization',
            address: 'Address',
            postalCode: 'Postal code',
            city: 'City',
            givenName: 'Given Name',
            familyName: 'Family Name',
            memberOf: '$t(g2.organization)',
            email: 'Email Address',
            phone: 'Phone Number',
            sendVerifyEmail: 'Send email verification to the email address',
            sendResetPassword: 'Send password reset email to the user',
            user: 'User',
            completeThePassword: 'You must provide the password',
            passWordLongerThan8: 'The password must be at least 8 characters long',
            samePasswordCheck: 'You must enter a password identical to the first one',
            newPassword: 'New Password',
            passwordCheck: 'Password Confirmation',
            cancel: 'Cancel',
            confirm: 'Confirm',
            password: 'Password',
            changePassword: 'Change Password',
            passwordChangement: 'Password Change',
            passwordEmail: "An email to change the password will be sent to your account's email address.",
            clear: 'Clear',
            accept: 'Accept',
            today: 'Today',
            addImages: 'Add one or more images',
            gallery: 'Gallery',
            save: 'Save',
            copySuccess: 'Already copied, click here to re-copy',
            copyToClipboard: 'Copy to clipboard',
            required: 'Required',
            noData: 'No data found',
            noOrganization: 'You are not included in an organization',
            toFilter: 'Filter',
            toFilterCount: 'Filter ({{count}})',
            apply: 'Apply',
            clearFilters: 'Clear filters',
            totalItem: '{{start}} - {{end}} of {{total}}',
            title: 'Title',
            text: 'Text'
        },
        http: {
            '600': '{{errorMessage}} due to a network issue',
            '401': 'You are trying to access a ressource you are not authorized to',
            '500': '{{errorMessage}} due to a technical issue, if this error occurs again please contact an administrator',
            backendErrors: {},
            errors: {
                organizationPage: 'The list of organizations could not be retrieved',
                organizationGet: 'The organization details could not be retrieved',
                organizationUpdate: 'The organization could not be updated',
                organizationCreate: 'The organization could not be created',
                organizationUploadLogo: "The organization's logo could not be uploaded",
                organizationDisable: 'The organization could not be deleted',
                userPage: 'The list of users could not be retrieved',
                userGet: 'User details could not be retrieved',
                userUpdate: 'The user could not be updated',
                userCreate: 'The user could not be created',
                userUpdatePassword: "The user's password could not be updated",
                userResetPassword: "The request to reset the user's password could not be sent",
                userUpdateEmail: "The user's email could not be updated",
                userDisable: 'The user could not be deleted'
            },
            success: {
                organizationUpdate: 'The organization has been updated successfully',
                organizationCreate: 'The organization has been created successfully',
                organizationDisable: 'The organization has been deleted successfully',
                userUpdate: 'The user has been updated successfully',
                userCreate: 'The user has been created successfully',
                userUpdatePassword: "The user's password has been updated successfully",
                userResetPassword: "The request to reset the user's password has been sent successfully",
                userDisable: 'The user has been deleted successfully'
            }
        }
    }
};

const initI18next = (languages, translationsOverrides, options) => {
    let fallbackLng = {};
    if (!languages) {
        fallbackLng = {
            'fr-FR': ['fr'],
            default: ['fr']
        };
    }
    else {
        let iteration = 0;
        for (const languageName in languages) {
            fallbackLng[languages[languageName.toString()]] = [languageName];
            if (iteration === 0) {
                fallbackLng['default'] = [languageName];
            }
            iteration++;
        }
    }
    i18n
        .use(Backend)
        .use(LanguageDetector)
        .use(initReactI18next)
        // init i18next
        // for all options read: https://www.i18next.com/overview/configuration-options
        .init(Object.assign({ returnNull: false, fallbackLng: fallbackLng, interpolation: {
            escapeValue: false // not needed for react as it escapes by default
        }, ns: ['translation', 'g2'], 
        // application translation is the default namespace
        defaultNS: 'translation', 
        // fallback to g2 translation to make g2 translations overridable
        fallbackNS: 'g2', react: {
            bindI18nStore: 'added'
        } }, options))
        .then(() => {
        // Fetch translations from the backend
        return i18n.reloadResources();
    })
        .then(() => {
        var _a, _b;
        // Merge translations from the backend with the initial translations
        i18n.addResourceBundle('fr', 'g2', Object.assign(Object.assign(Object.assign({}, G2Translations.fr), translationsOverrides === null || translationsOverrides === void 0 ? void 0 : translationsOverrides.fr), { g2: Object.assign(Object.assign({}, G2Translations.fr.g2), (_a = translationsOverrides === null || translationsOverrides === void 0 ? void 0 : translationsOverrides.fr) === null || _a === void 0 ? void 0 : _a.g2) }), true, false);
        i18n.addResourceBundle('en', 'g2', Object.assign(Object.assign(Object.assign({}, G2Translations.en), translationsOverrides === null || translationsOverrides === void 0 ? void 0 : translationsOverrides.en), { g2: Object.assign(Object.assign({}, G2Translations.en.g2), (_b = translationsOverrides === null || translationsOverrides === void 0 ? void 0 : translationsOverrides.en) === null || _b === void 0 ? void 0 : _b.g2) }), true, false);
    });
    return i18n;
};

const AppProvider = (props) => {
    const { i18nOverride, languages = { fr: 'fr-FR' }, loadingComponent, queryClient, children, i18nTranslationsOverrides, i18nOptions } = props;
    const i18n = useMemo(() => i18nOverride ||
        initI18next(languages, i18nTranslationsOverrides, i18nOptions), [i18nOverride, languages, i18nTranslationsOverrides, i18nOptions]);
    return (React__default.createElement(Suspense, { fallback: loadingComponent !== null && loadingComponent !== void 0 ? loadingComponent : React__default.createElement(LoadingProviders, null) },
        React__default.createElement(QueryClientProvider, { client: queryClient },
            React__default.createElement(I18nextProvider, { i18n: i18n },
                React__default.createElement(MantineProvider, null,
                    React__default.createElement(BrowserRouter, null, children))))));
};

var _g, _defs;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
var Svg404 = function Svg404(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 1774 865"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    filter: "url(#404_svg__a)"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "#353945",
    d: "M.55 626.18 243.716 8.016h150.879v585.449h77.148v99.121h-77.148V842H276.918V692.586H.551zm276.368-32.715V470.418l11.719-314.453h-14.649l-144.531 437.5zm267.461-347.168q0-54.687 9.766-94.238 10.253-40.04 26.367-66.895 16.601-27.345 38.086-43.945 21.484-16.602 43.945-25.39 22.949-9.279 45.41-12.208 22.95-2.93 41.992-2.93 18.067 0 40.039 2.93 22.461 2.442 44.922 11.23 22.95 8.301 43.946 24.903 21.484 16.113 37.597 43.457 16.602 27.344 26.367 67.383 10.254 40.039 10.254 95.703v352.539q0 54.688-10.254 94.726-9.765 39.552-26.367 67.383-16.601 27.833-38.086 45.41-21.484 17.091-43.945 26.856-22.461 9.278-44.922 12.695-21.972 3.418-39.551 3.418-19.532 0-42.48-3.418-22.95-3.417-45.899-12.695-22.948-9.765-44.433-26.856-20.996-17.09-37.11-44.921-16.113-27.832-25.878-67.872-9.766-40.038-9.766-94.726zm129.394 405.762q0 51.758 18.555 75.195 19.043 23.437 57.617 23.437 37.598 0 56.153-23.437 18.554-23.437 18.554-75.195V200.398q0-54.687-18.554-79.589-18.555-24.903-56.153-24.903-38.574 0-57.617 24.903-18.555 24.902-18.555 79.589zm365.607-25.879L1282.54 8.016h150.88v585.449h77.15v99.121h-77.15V842h-117.67V692.586h-276.37zm276.37-32.715V470.418l11.71-314.453h-14.64l-144.53 437.5zm296.27 102.539h145.99V842h-145.99z"
  }))), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("filter", {
    id: "404_svg__a",
    width: 1772.46,
    height: 863.633,
    x: 0.551,
    y: 0.691,
    colorInterpolationFilters: "sRGB",
    filterUnits: "userSpaceOnUse"
  }, /*#__PURE__*/React.createElement("feFlood", {
    floodOpacity: 0,
    result: "BackgroundImageFix"
  }), /*#__PURE__*/React.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), /*#__PURE__*/React.createElement("feOffset", {
    dx: 15,
    dy: 15
  }), /*#__PURE__*/React.createElement("feColorMatrix", {
    values: "0 0 0 0 0.575 0 0 0 0 0.575 0 0 0 0 0.575 0 0 0 0.5 0"
  }), /*#__PURE__*/React.createElement("feBlend", {
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), /*#__PURE__*/React.createElement("feBlend", {
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  })))));
};

const NotFoundIcon = React__default.forwardRef((props, ref) => {
    return React__default.createElement(Svg404, Object.assign({ ref: ref }, props));
});

const useStyles = makeG2STyles()((theme) => ({
    notFoundIcon: {
        maxWidth: '400px',
        width: '80vw'
    },
    title: {
        margin: theme.spacing * 2,
        '@media (max-width:800px)': {
            fontSize: '35px'
        },
        '@media (max-width:400px)': {
            fontSize: '30px'
        }
    }
}));
const NoMatchPage = (props) => {
    const { title = 'Page not found', buttonLabel = 'Go back home', noGoBack = false, classes, styles, className, style, id } = props, other = __rest(props, ["title", "buttonLabel", "noGoBack", "classes", "styles", "className", "style", "id"]);
    const defaultStyles = useStyles();
    return (React__default.createElement(Box, Object.assign({ className: className, style: style, id: id, display: 'flex', width: '100%', height: '100%', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '20px' }, other),
        React__default.createElement(NotFoundIcon, { className: defaultStyles.cx(defaultStyles.classes.notFoundIcon, classes === null || classes === void 0 ? void 0 : classes.notFoundIcon), style: styles === null || styles === void 0 ? void 0 : styles.notFoundIcon }),
        React__default.createElement(Typography, { variant: 'h3', className: defaultStyles.cx(defaultStyles.classes.title, classes === null || classes === void 0 ? void 0 : classes.title), style: styles === null || styles === void 0 ? void 0 : styles.title }, title),
        !noGoBack && (React__default.createElement(Button, { className: classes === null || classes === void 0 ? void 0 : classes.backButton, style: styles === null || styles === void 0 ? void 0 : styles.backButton, component: Link, componentProps: { to: '/' }, variant: 'outlined' }, buttonLabel))));
};

const Impersonate = () => {
    const { keycloak } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        navigate('/');
        keycloak.login();
    }, []);
    return React__default.createElement(React__default.Fragment, null);
};

const Router = (props) => {
    const { children, noMatchComponent, noMatchPageProps } = props, other = __rest(props, ["children", "noMatchComponent", "noMatchPageProps"]);
    return (React__default.createElement(Routes, Object.assign({}, other),
        children,
        React__default.createElement(Route, { path: '/impersonate', element: React__default.createElement(Impersonate, null) }),
        React__default.createElement(Route, { path: '*', element: noMatchComponent || React__default.createElement(NoMatchPage, Object.assign({}, noMatchPageProps)) })));
};

const PrivateRoute = (props) => {
    const { roles, unauthorizedComponent } = props, other = __rest(props, ["roles", "unauthorizedComponent"]);
    const { service } = useAuth();
    return service.hasRole(roles) ? (
    //@ts-ignore
    React__default.createElement(Route, Object.assign({}, other))) : (unauthorizedComponent || React__default.createElement(NoMatchPage, null));
};

const useI18n = () => {
    const { i18n } = useTranslation();
    return { i18n: i18n };
};

const ReduxProvider = (props) => {
    const { reduxStore, children } = props;
    return React__default.createElement(Provider, { store: reduxStore }, children);
};

/**
 * initiliaze the instance of the redux store
 * @param {Reducers extends { [key: string]: Reducer<any, AnyAction> }}  reducers - an object containing the reducers you want to insert in your store like that: `{myReducer: myReducerObject}`
 * @param {Middleware<{}, any, any>[]}  middlewares - An array containing the possible middlewares you want to add to your store
 * @return {Store} Return the redux store instance
 */
const initRedux = (reducers, middlewares) => {
    middlewares = middlewares !== null && middlewares !== void 0 ? middlewares : [];
    const reducersCombination = reducers !== null && reducers !== void 0 ? reducers : {};
    const reducer = combineReducers(reducersCombination);
    const composeEnhancer = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    const store = createStore(reducer, composeEnhancer(applyMiddleware(...middlewares)));
    return { store: store, reducer: reducer };
};

export { AppProvider, G2ConfigBuilder, G2Translations, KeycloakProvider, NoMatchPage, PrivateRoute, ReduxProvider, Router, fsConfig, g2Config, imConfig, initI18next, initRedux, platFormConfig, useAuth, useAuthenticatedRequest, useI18n, useNoAuthenticatedRequest };
//# sourceMappingURL=index.es.js.map
